import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ACCOUNT_QUERY_KEY, confirmNotificationEmail } from '../lib/account-api.js'
import { SUBMIT_ERROR_FALLBACK, USER_MESSAGES, toUserMessage } from '../lib/error-copy.js'
import { LOADING } from '../lib/labels.js'

/**
 * 通知先メールアドレスの確認メールのリンク先（Issue #118）。ログインしていなくても開ける。
 *
 * 開いただけでは切り替えず、ボタンを押してから切り替える。メールソフトやセキュリティ製品が
 * リンクを先読みすることがあり、開いただけで切り替わると本人が押す前に確定してしまうため。
 */
export function NotificationEmailConfirmPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => confirmNotificationEmail(token),
    // ログイン中なら設定画面の表示（確認待ち）を新しい状態にする
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY }),
  })

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center space-y-5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          通知先メールアドレスの確認
        </h1>
        <ConfirmBody token={token} mutation={mutation} />
        <Link to="/settings" className="block text-xs text-muted-foreground hover:underline">
          設定画面へ
        </Link>
      </div>
    </div>
  )
}

type ConfirmMutation = {
  mutate: () => void
  isPending: boolean
  isSuccess: boolean
  error: Error | null
  data: { notificationEmail: string } | undefined
}

function ConfirmBody({ token, mutation }: { token: string; mutation: ConfirmMutation }) {
  if (!token) {
    return <p className="text-sm text-destructive">{USER_MESSAGES.notificationEmailLinkInvalid}</p>
  }
  if (mutation.isSuccess && mutation.data) {
    return (
      <p role="status" className="text-sm">
        これからのお知らせは{' '}
        <span className="font-medium break-all">{mutation.data.notificationEmail}</span>{' '}
        に届きます。
      </p>
    )
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        「切り替える」を押すと、このメールアドレスに通知が届くようになります。
      </p>
      {mutation.error && (
        <p
          role="alert"
          className="text-sm text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2"
        >
          {toUserMessage(mutation.error, SUBMIT_ERROR_FALLBACK)}
        </p>
      )}
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {mutation.isPending ? LOADING.saving : '切り替える'}
      </button>
    </div>
  )
}
